import { type CityConfig, getCity, listCities } from "../../../config/cities.ts";

/** The URL owns city selection; old root and /about links always open Almaty. */
export function cityForPath(pathname: string): CityConfig {
  const id = pathname.split("/")[1];
  return listCities().find((city) => city.id === id) ?? getCity("almaty");
}
/**
 * A city that is not live yet (status `preview`, D45) is closed on the public sites until the owner launches it: only
 * a local build (the T-105 review gallery) or an explicit `?preview=1` opens it.
 */
export function cityOpen(
  city: CityConfig,
  location: { hostname: string; search: string },
): boolean {
  if (city.status === "live") return true;
  if (/^(localhost|127\.0\.0\.1|\[::1\]|::1)$/.test(location.hostname)) return true;
  if (location.hostname.endsWith(".localhost")) return true;
  return new URLSearchParams(location.search).get("preview") === "1";
}
/** The URL state is rewritten on every change; `?preview=1` stays in it, so a reload or a shared link stays open. */
export function keepPreview(query: string, search: string): string {
  if (new URLSearchParams(search).get("preview") !== "1") return query;
  return query.length > 0 ? `${query}&preview=1` : "?preview=1";
}
/** The cities this visitor may open here, in the registry's order (Almaty first). */
export function openCities(location: { hostname: string; search: string }): CityConfig[] {
  return listCities().filter((city) => cityOpen(city, location));
}
/** The site that serves every city under /<id>/ (T-105g); a mirror (Vercel) serves Almaty only. */
export const CITY_SITE = "https://ainalma.kz";
/**
 * The link to a city's map. A preview city keeps `?preview=1` (whoever sees the link may open it),
 * and so does any link from a preview session. From the Almaty map of another host a second city is
 * on the city site; a second city's own map is always on a host that serves them all.
 */
export function cityHref(
  city: CityConfig,
  location: { hostname: string; search: string },
  current: CityConfig = MAP_CITY,
): string {
  const path = city.id === "almaty" ? "/" : `/${city.id}/`;
  const preview =
    city.status === "preview" || new URLSearchParams(location.search).get("preview") === "1";
  const served =
    city.id === "almaty" ||
    current.id !== "almaty" ||
    location.hostname === new URL(CITY_SITE).hostname;
  return `${served ? "" : CITY_SITE}${path}${preview ? "?preview=1" : ""}`;
}
/** The city whose bbox holds the point; the registry's bboxes are the routing data, so outside it a map has nothing. */
export function cityAt(
  point: { lat: number; lon: number },
  cities: readonly CityConfig[],
): CityConfig | null {
  return (
    cities.find(
      ({ bbox: b }) =>
        point.lon >= b.west && point.lon <= b.east && point.lat >= b.south && point.lat <= b.north,
    ) ?? null
  );
}
export let MAP_CITY = cityForPath(typeof window === "undefined" ? "/" : window.location.pathname);
export const CITY_BASE = MAP_CITY.id === "almaty" ? "" : `/${MAP_CITY.id}`;
export const cityAsset = (path: string): string => `${CITY_BASE}/${path.replace(/^\/+/, "")}`;
export const cityStorageKey = (key: string): string =>
  MAP_CITY.id === "almaty" ? key : `${key}.${MAP_CITY.id}`;

export function cityContains(point: { lat: number; lon: number }): boolean {
  const b = MAP_CITY.bbox;
  return point.lon >= b.west && point.lon <= b.east && point.lat >= b.south && point.lat <= b.north;
}

/** Review bundles can use a registered mini bbox; never silently fall back to another city. */
export function configureCityManifest(value: unknown): void {
  if (typeof value !== "object" || value === null) throw new Error("Invalid city manifest");
  const manifest = value as Record<string, unknown>;
  const preset = Object.values(MAP_CITY.bboxes).find((b) => b.id === manifest.bboxId);
  if (
    manifest.city !== MAP_CITY.id ||
    !preset ||
    manifest.bbox === null ||
    typeof manifest.bbox !== "object"
  )
    throw new Error("City manifest mismatch");
  const bbox = manifest.bbox as Record<string, unknown>;
  for (const key of ["south", "west", "north", "east"] as const)
    if (bbox[key] !== preset[key]) throw new Error("City manifest bbox mismatch");
  const features = manifest.features;
  if (
    !features ||
    typeof features !== "object" ||
    Object.entries(MAP_CITY.features).some(
      ([key, value]) => (features as Record<string, unknown>)[key] !== value,
    )
  )
    throw new Error("City manifest features mismatch");
  MAP_CITY = {
    ...MAP_CITY,
    origin: { lon: (preset.west + preset.east) / 2, lat: (preset.south + preset.north) / 2 },
    graphId: preset.id,
    bboxId: preset.id,
    bbox: { south: preset.south, west: preset.west, north: preset.north, east: preset.east },
    ...(preset.id === MAP_CITY.bboxId
      ? {}
      : {
          view: {
            center: [(preset.west + preset.east) / 2, (preset.south + preset.north) / 2],
            zoom: 14,
          },
        }),
  };
}
